import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FaCompass, FaGift, FaMapMarkedAlt, FaBolt, FaShieldAlt, FaArrowRight, FaCheckCircle, FaStore, FaUsers } from 'react-icons/fa';

const copy = {
  fr: {
    badge: 'Guide Forfeo',
    titleA: 'Comment', titleB: 'Forfeo Explore', titleC: 'fonctionne',
    subtitle: 'Une seule destination numérique pour découvrir les expériences locales, profiter des offres intelligentes et réserver des moments authentiques au Québec.',
    ctaExplore: 'Explorer les offres', ctaTrust: 'Voir le centre de confiance',
    stepsTitle: 'Un parcours simple, local et sécurisé',
    steps: [
      { icon: 'compass', title: 'Explore les expériences', text: 'Parcours les offres, régions, forfaits, hébergements, Last Call et bons plans locaux disponibles sur Forfeo Explore.' },
      { icon: 'gift', title: 'Choisis une offre ou une carte', text: 'Sélectionne une expérience, une carte numérique ou une alerte voyage selon ton budget et ton style.' },
      { icon: 'shield', title: 'Réserve ou achète de façon sécurisée', text: 'Les paiements, confirmations, QR codes et réservations sont gérés par l’écosystème technologique Forfeo.' },
      { icon: 'bolt', title: 'Vis l’expérience sur place', text: 'Présente ta confirmation au partenaire, suis l’itinéraire et profite d’une expérience locale réelle.' },
    ],
    usersTitle: 'Pour les voyageurs, les locaux et les entreprises',
    users: [
      { title: 'Voyageurs', text: 'Trouvez rapidement des idées, itinéraires, offres et expériences qui transforment un séjour au Québec.' },
      { title: 'Locaux', text: 'Redécouvrez votre région avec des forfaits, cartes, offres de dernière minute et avantages Forfeo.' },
      { title: 'Partenaires', text: 'Gagnez en visibilité numérique, simplifiez vos ventes et rejoignez un réseau local orienté expérience.' },
    ],
    finalTitle: 'Prêt à commencer ?',
    finalText: 'Découvre les offres actives ou inscris ton entreprise pour rejoindre le réseau Forfeo.',
    partnerCta: 'Devenir partenaire'
  },
  en: {
    badge: 'Forfeo Guide',
    titleA: 'How', titleB: 'Forfeo Explore', titleC: 'works',
    subtitle: 'A single digital destination to discover local experiences, unlock smart offers, and book authentic moments across Quebec.',
    ctaExplore: 'Explore offers', ctaTrust: 'View trust center',
    stepsTitle: 'A simple, local and secure journey',
    steps: [
      { icon: 'compass', title: 'Explore experiences', text: 'Browse offers, regions, packages, stays, Last Call deals and local discoveries available on Forfeo Explore.' },
      { icon: 'gift', title: 'Choose an offer or card', text: 'Select an experience, a digital card or a travel alert based on your budget and style.' },
      { icon: 'shield', title: 'Book or buy securely', text: 'Payments, confirmations, QR codes and reservations are handled through the Forfeo technology ecosystem.' },
      { icon: 'bolt', title: 'Enjoy the experience on site', text: 'Show your confirmation to the partner, follow the itinerary and enjoy a real local experience.' },
    ],
    usersTitle: 'Built for travelers, locals and businesses',
    users: [
      { title: 'Travelers', text: 'Find ideas, itineraries, offers and experiences that turn a Quebec stay into something memorable.' },
      { title: 'Locals', text: 'Rediscover your region with packages, digital cards, last-minute deals and Forfeo benefits.' },
      { title: 'Partners', text: 'Increase digital visibility, simplify sales and join a local network built around experiences.' },
    ],
    finalTitle: 'Ready to start?',
    finalText: 'Explore active offers or register your business to join the Forfeo network.',
    partnerCta: 'Become a partner'
  }
};

const iconMap: Record<string, React.ReactNode> = {
  compass: <FaCompass />,
  gift: <FaGift />,
  shield: <FaShieldAlt />,
  bolt: <FaBolt />,
};

export default function CommentCaMarche() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'fr';
  const t = copy[lang];

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans">
      <Header />
      <main className="pt-28 pb-24 overflow-hidden">
        <section className="relative px-4">
          <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,#dbeafe,transparent_35%),radial-gradient(circle_at_top_right,#fef3c7,transparent_30%)]" />
          <div className="max-w-6xl mx-auto text-center py-20">
            <div className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest mb-8">
              <FaCompass className="text-cyan-300" /> {t.badge}
            </div>
            <h1 className="text-5xl md:text-7xl font-black tracking-tight mb-6">
              {t.titleA} <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">{t.titleB}</span> {t.titleC}
            </h1>
            <p className="max-w-3xl mx-auto text-lg md:text-xl text-slate-600 leading-relaxed mb-10">{t.subtitle}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/boutique" className="px-8 py-4 rounded-2xl bg-slate-900 text-white font-black hover:bg-slate-800 transition flex items-center justify-center gap-3">
                {t.ctaExplore} <FaArrowRight />
              </Link>
              <Link to="/confiance" className="px-8 py-4 rounded-2xl bg-white border border-slate-200 text-slate-800 font-black hover:bg-slate-50 transition">
                {t.ctaTrust}
              </Link>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 py-16">
          <h2 className="text-3xl md:text-4xl font-black text-center mb-12">{t.stepsTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.steps.map((step, index) => (
              <div key={step.title} className="bg-white border border-slate-100 rounded-3xl p-6 shadow-xl shadow-slate-200/40 hover:-translate-y-1 transition">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 text-white flex items-center justify-center text-xl mb-5 shadow-lg">
                  {iconMap[step.icon]}
                </div>
                <div className="text-xs font-black text-blue-600 uppercase tracking-widest mb-2">0{index + 1}</div>
                <h3 className="text-xl font-black mb-3">{step.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{step.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 py-16">
          <div className="bg-slate-950 text-white rounded-[36px] p-8 md:p-12 overflow-hidden relative">
            <div className="absolute -right-24 -top-24 w-80 h-80 rounded-full bg-cyan-500/20 blur-3xl" />
            <h2 className="text-3xl md:text-4xl font-black mb-8 relative">{t.usersTitle}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 relative">
              {t.users.map((item, index) => (
                <div key={item.title} className="bg-white/5 border border-white/10 rounded-3xl p-6 backdrop-blur">
                  <div className="w-11 h-11 rounded-2xl bg-white text-slate-950 flex items-center justify-center mb-4">
                    {index === 0 ? <FaMapMarkedAlt /> : index === 1 ? <FaUsers /> : <FaStore />}
                  </div>
                  <h3 className="font-black text-xl mb-2">{item.title}</h3>
                  <p className="text-slate-300 text-sm leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 text-center py-16">
          <FaCheckCircle className="text-green-500 text-5xl mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-black mb-4">{t.finalTitle}</h2>
          <p className="text-slate-600 mb-8">{t.finalText}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/boutique" className="px-7 py-4 rounded-2xl bg-blue-600 text-white font-black hover:bg-blue-700 transition">{t.ctaExplore}</Link>
            <Link to="/business" className="px-7 py-4 rounded-2xl bg-slate-100 text-slate-900 font-black hover:bg-slate-200 transition">{t.partnerCta}</Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
